import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * 7 Colores: hover handling that tolerates moving the mouse from the menu bar to the dropdown.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #deactivateTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #switchTimer;

  /** Last known pointer position */
  #pointer = { x: -1, y: -1 };

  /** True when a close was requested while the pointer was still over the open menu */
  #closePending = false;

  /** True while a delayed close is waiting to run */
  #deactivatePending = false;

  /** Menu item waiting to be opened (hover intent) */
  /** @type {Element | null} */
  #switchTarget = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    document.addEventListener('pointermove', this.#pointerMoveListener, { passive: true });
    document.documentElement.addEventListener('pointerleave', this.#documentLeaveListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#deactivateTimer);
    clearTimeout(this.#switchTimer);
    document.removeEventListener('pointermove', this.#pointerMoveListener);
    document.documentElement.removeEventListener('pointerleave', this.#documentLeaveListener);
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * Track the pointer and close a pending menu once the pointer really leaves it.
   * @param {PointerEvent} event
   */
  #pointerMoveListener = (event) => {
    this.#pointer.x = event.clientX;
    this.#pointer.y = event.clientY;

    if (event.pointerType === 'touch') return;

    // Pointer events on the menu items are not always reliable, so the pointer position is also used.
    const listItem = this.#topLevelItemAtPointer();
    if (listItem) {
      const item = findMenuItem(listItem);
      if (item && item === this.#state.activeItem) {
        clearTimeout(this.#deactivateTimer);
        clearTimeout(this.#switchTimer);
        this.#deactivatePending = false;
        this.#closePending = false;
        this.#switchTarget = null;
      } else if (item && this.#switchTarget !== listItem) {
        this.#switchTarget = listItem;
        this.activate({ type: 'pointerenter', target: listItem });
      }
      return;
    }

    this.#switchTarget = null;

    if (this.#state.activeItem && !this.#deactivatePending && !this.#isPointerOverActiveMenu()) {
      this.#scheduleDeactivate();
    }
  };

  /**
   * The visible top-level menu item under the pointer, if any.
   * @returns {Element | null}
   */
  #topLevelItemAtPointer() {
    const { x, y } = this.#pointer;
    for (const listItem of this.querySelectorAll('li.menu-list__list-item')) {
      if (listItem.getAttribute('slot') === 'overflow') continue;
      const r = listItem.getBoundingClientRect();
      if (r.width > 0 && r.height > 0 && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
        return listItem;
      }
    }
    return null;
  }

  #documentLeaveListener = () => {
    clearTimeout(this.#deactivateTimer);
    clearTimeout(this.#switchTimer);
    this.#closePending = false;
    this.#deactivate();
  };

  /**
   * Whether the pointer is over the active menu item, its dropdown panel or the gap between them.
   * Uses coordinates instead of pointer events, which can fire spuriously while the panel opens.
   * @returns {boolean}
   */
  #isPointerOverActiveMenu() {
    const item = this.#state.activeItem;
    const { x, y } = this.#pointer;
    if (!item || x < 0 || y < 0) return false;

    /** @param {DOMRect | undefined} r */
    const inside = (r) => !!r && r.width > 0 && r.height > 0 && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;

    const itemRect = item.closest('li')?.getBoundingClientRect();
    if (inside(itemRect)) return true;

    const submenu = findSubmenu(item);
    const panel = submenu?.querySelector('.menu-list__submenu-inner') ?? submenu;
    const panelRect = panel?.getBoundingClientRect();
    if (!panelRect || panelRect.height === 0) return false;
    if (inside(panelRect)) return true;

    // Gap between the menu bar and the dropdown panel
    return !!itemRect && y >= itemRect.bottom - 2 && y <= panelRect.top + 1 && x >= panelRect.left && x <= panelRect.right;
  }

  /**
   * Close the active menu after a short delay, unless the pointer is still over it.
   */
  #scheduleDeactivate() {
    clearTimeout(this.#deactivateTimer);
    this.#deactivatePending = true;
    this.#deactivateTimer = setTimeout(() => {
      this.#deactivatePending = false;
      if (this.#isPointerOverActiveMenu()) {
        this.#closePending = true;
        return;
      }
      this.#closePending = false;
      this.#deactivate();
    }, 150);
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item. When another item is already open, wait briefly so that
   * moving the mouse diagonally towards the dropdown does not switch to a neighbouring item.
   * @param {PointerEvent | FocusEvent | { type: string, target: Element }} event
   */
  activate = (event) => {
    clearTimeout(this.#deactivateTimer);
    clearTimeout(this.#switchTimer);
    this.#deactivatePending = false;
    this.#closePending = false;

    const target = event.target;
    const nextItem = target instanceof Element ? findMenuItem(target) : null;
    const isSwitching =
      event.type === 'pointerenter' && this.#state.activeItem && nextItem && nextItem !== this.#state.activeItem;

    if (isSwitching && target instanceof Element) {
      const type = event.type;
      this.#switchTimer = setTimeout(() => {
        this.#switchTarget = null;
        const r = target.getBoundingClientRect();
        const { x, y } = this.#pointer;
        if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
          this.#activateNow({ type, target });
        }
      }, 120);
      return;
    }

    this.#activateNow(event);
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | { type: string, target: Element }} event
   */
  #activateNow = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    clearTimeout(this.#switchTimer);
    this.#scheduleDeactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
